import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Check, Shield, Building2, Building, Wallet, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { setToken as setStoredToken } from '../services/tokenService';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PLANS = {
  adherent: {
    id: 'COPPAM_ADHERENT',
    name: 'Adhérent COPPAM',
    price: 29,
    credits: 30,
    icon: Shield,
    color: 'emerald',
    description: 'Artisans & TPE',
    features: ['Simulations standards', 'Attestations solidaires', 'Support email']
  },
  professionnel: {
    id: 'OSCOP_PROFESSIONNEL',
    name: 'Professionnel O\'SCOP',
    price: 79,
    credits: 120,
    icon: Building2,
    color: 'blue',
    description: 'PME & Coopératives',
    features: ['Tout COPPAM', 'Avenants & personnalisations', 'Support prioritaire']
  },
  institution: {
    id: 'FOGEDOM_INSTITUTION',
    name: 'Institution FOGEDOM',
    price: 199,
    credits: 350,
    icon: Building,
    color: 'cyan',
    description: 'Collectivités & Réseaux',
    features: ['Tout O\'SCOP', 'API & Marque blanche', 'Account manager dédié']
  }
};

const RegisterESS = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialPlan = searchParams.get('plan') || 'adherent';
  
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    organisation: '',
    nom: '',
    prenom: '',
    telephone: '',
    siret: '',
    territoire: 'guadeloupe',
    acceptTerms: false
  });

  const [errors, setErrors] = useState({});

  // Check email availability
  const checkEmail = async (email) => {
    if (!email || !email.includes('@')) return;
    setCheckingEmail(true);
    try {
      const res = await fetch(`${API}/api/onboarding/check-email/${encodeURIComponent(email)}`);
      const data = await res.json();
      setEmailAvailable(data.available);
    } catch {
    } finally {
      setCheckingEmail(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.email) checkEmail(formData.email);
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.email]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email requis';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email invalide';
    else if (emailAvailable === false) newErrors.email = 'Un compte existe déjà avec cet email';
    
    if (!formData.password) newErrors.password = 'Mot de passe requis';
    else if (formData.password.length < 8) newErrors.password = 'Minimum 8 caractères';
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.organisation) newErrors.organisation = 'Organisation requise';
    if (!formData.nom) newErrors.nom = 'Nom requis';
    if (!formData.acceptTerms) newErrors.acceptTerms = 'Vous devez accepter les conditions';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    
    setLoading(true);
    try {
      const planConfig = PLANS[selectedPlan];
      
      const response = await fetch(`${API}/api/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          organisation: formData.organisation,
          plan: planConfig.id,
          nom: formData.nom,
          prenom: formData.prenom,
          telephone: formData.telephone,
          siret: formData.siret,
          territoire: formData.territoire
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Stocker le token
        if (data.token) {
          setStoredToken(data.token);
          localStorage.setItem('user', JSON.stringify({
            id: data.user_id,
            email: formData.email,
            wallet_id: data.wallet_id,
            plan: data.plan
          }));
        }
        
        toast.success(`🎉 Bienvenue ! ${data.initial_credits} crédits ajoutés à votre wallet.`);
        
        // Redirection vers le dashboard
        setTimeout(() => {
          navigate(data.redirect_url || '/member/dashboard');
        }, 1500);
      } else {
        toast.error(data.detail || 'Erreur lors de la création du compte');
      }
    } catch {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const plan = PLANS[selectedPlan];
  const _PlanIcon = plan.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-800">O'SCOP</span>
          </Link>
          <Link to="/login" className="text-slate-600 hover:text-emerald-600">
            Déjà un compte ? Se connecter
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Progress */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex items-center gap-4">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                  step >= s ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {step > s ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-20 h-1 rounded ${step > s ? 'bg-emerald-600' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step 1: Choix du plan */}
        {step === 1 && (
          <div>
            <h1 className="text-3xl font-bold text-center text-slate-800 mb-2">
              Choisissez votre offre
            </h1>
            <p className="text-center text-slate-600 mb-8">
              Sélectionnez le plan adapté à vos besoins
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {Object.entries(PLANS).map(([key, p]) => {
                const Icon = p.icon;
                const isSelected = selectedPlan === key;
                const colors = {
                  emerald: 'border-emerald-500 bg-emerald-50',
                  blue: 'border-blue-500 bg-blue-50',
                  cyan: 'border-cyan-500 bg-cyan-50'
                };
                
                return (
                  <div
                    key={key}
                    onClick={() => setSelectedPlan(key)}
                    className={`relative cursor-pointer rounded-2xl border-2 p-6 transition-all hover:shadow-lg ${
                      isSelected ? colors[p.color] : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute -top-3 -right-3 w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                    )}
                    
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      p.color === 'emerald' ? 'bg-emerald-100' : 
                      p.color === 'blue' ? 'bg-blue-100' : 'bg-cyan-100'
                    }`}>
                      <Icon className={`w-6 h-6 ${
                        p.color === 'emerald' ? 'text-emerald-600' : 
                        p.color === 'blue' ? 'text-blue-600' : 'text-cyan-600'
                      }`} />
                    </div>
                    
                    <h3 className="font-bold text-lg text-slate-800">{p.name}</h3>
                    <p className="text-sm text-slate-500 mb-3">{p.description}</p>
                    
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-3xl font-bold text-slate-800">{p.price}€</span>
                      <span className="text-slate-500">/mois</span>
                    </div>
                    
                    <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2 mb-4">
                      <Wallet className="w-4 h-4 text-slate-600" />
                      <span className="text-sm font-medium text-slate-700">{p.credits} crédits/mois</span>
                    </div>
                    
                    <ul className="space-y-2">
                      {p.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                          <Check className="w-4 h-4 text-emerald-500" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => setStep(2)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-xl font-semibold transition-all"
              >
                Continuer avec {plan.name}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Compte */}
        {step === 2 && (
          <div className="max-w-lg mx-auto">
            <h1 className="text-3xl font-bold text-center text-slate-800 mb-2">
              Créez votre compte
            </h1>
            <p className="text-center text-slate-600 mb-8">
              {plan.name} - {plan.credits} crédits inclus
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email professionnel</label>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                        errors.email ? 'border-red-500' : emailAvailable === true ? 'border-green-500' : 'border-slate-200'
                      }`}
                      placeholder="vous@entreprise.com"
                    />
                    {checkingEmail && (
                      <Loader2 className="absolute right-3 top-3.5 w-5 h-5 text-slate-400 animate-spin" />
                    )}
                    {emailAvailable === true && !checkingEmail && (
                      <Check className="absolute right-3 top-3.5 w-5 h-5 text-green-500" />
                    )}
                  </div>
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 ${
                        errors.password ? 'border-red-500' : 'border-slate-200'
                      }`}
                      placeholder="Minimum 8 caractères"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Confirmer le mot de passe</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 ${
                      errors.confirmPassword ? 'border-red-500' : 'border-slate-200'
                    }`}
                  />
                  {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 border border-slate-200 text-slate-600 py-3 rounded-xl font-medium hover:bg-slate-50"
                >
                  Retour
                </button>
                <button
                  onClick={() => validateStep1() && setStep(3)}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-semibold"
                >
                  Continuer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Organisation */}
        {step === 3 && (
          <div className="max-w-lg mx-auto">
            <h1 className="text-3xl font-bold text-center text-slate-800 mb-2">
              Votre organisation
            </h1>
            <p className="text-center text-slate-600 mb-8">
              Dernière étape avant d'activer votre wallet
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="space-y-4">
                {/* Organisation */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nom de l'organisation *</label>
                  <input
                    type="text"
                    name="organisation"
                    value={formData.organisation}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 ${
                      errors.organisation ? 'border-red-500' : 'border-slate-200'
                    }`}
                    placeholder="Entreprise / Coopérative"
                  />
                  {errors.organisation && <p className="text-red-500 text-sm mt-1">{errors.organisation}</p>}
                </div>

                {/* Nom / Prénom */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 ${
                        errors.nom ? 'border-red-500' : 'border-slate-200'
                      }`}
                    />
                    {errors.nom && <p className="text-red-500 text-sm mt-1">{errors.nom}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Prénom</label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                {/* SIRET */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">SIRET (optionnel)</label>
                  <input
                    type="text"
                    name="siret"
                    value={formData.siret}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                    placeholder="14 chiffres"
                  />
                </div>

                {/* Territoire */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Territoire</label>
                  <select
                    name="territoire"
                    value={formData.territoire}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="guadeloupe">Guadeloupe (971)</option>
                    <option value="martinique">Martinique (972)</option>
                    <option value="guyane">Guyane (973)</option>
                    <option value="reunion">La Réunion (974)</option>
                    <option value="mayotte">Mayotte (976)</option>
                    <option value="metropole">Métropole</option>
                  </select>
                </div>

                {/* Terms */}
                <div className="flex items-start gap-3 mt-4">
                  <input
                    type="checkbox"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    className="mt-1 w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                  />
                  <label className="text-sm text-slate-600">
                    J'accepte les <a href="/cgu" className="text-emerald-600 underline">conditions générales</a> et 
                    la <a href="/privacy" className="text-emerald-600 underline">politique de confidentialité</a>.
                    <br />
                    <span className="text-xs text-slate-500">
                      Les crédits permettent l'activation de services numériques et documentaires. 
                      Ils ne constituent ni une prime, ni une cotisation d'assurance.
                    </span>
                  </label>
                </div>
                {errors.acceptTerms && <p className="text-red-500 text-sm">{errors.acceptTerms}</p>}
              </div>

              {/* Récap */}
              <div className="mt-6 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-800">{plan.name}</p>
                    <p className="text-sm text-slate-600">{plan.credits} crédits inclus</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-emerald-600">{plan.price}€</p>
                    <p className="text-xs text-slate-500">/mois</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 border border-slate-200 text-slate-600 py-3 rounded-xl font-medium hover:bg-slate-50"
                >
                  Retour
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Création en cours...
                    </>
                  ) : (
                    <>
                      <Wallet className="w-5 h-5" />
                      Activer mon wallet
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterESS;
